// Pure selection-state logic for the broadcast quote request basket
// (ServiceList.tsx). Extracted so the swap/dedupe rule can be unit tested
// without mounting the full search-results page.
//
// Max ONE service per businessId in the basket — pms rejects a second at
// submit time (DUPLICATE_BUSINESS) since a business, not the provider
// account that owns it, is the unit a customer is comparing. One provider
// account can legitimately own multiple businesses, so dedupe must key on
// businessId, never providerId.

export interface BroadcastSelectionEntry {
  serviceId: string;
  providerId: string;
  businessId: string;
}

export type BroadcastSelectionMap = Map<string, BroadcastSelectionEntry>;

export const MAX_BROADCAST_SELECTIONS = 5;

export interface ApplyBroadcastSelectionResult {
  selection: BroadcastSelectionMap;
  /** True when this change replaced a different card from the SAME business. */
  swapped: boolean;
}

/**
 * Applies a single checkbox toggle to the broadcast basket.
 *
 * - Deselecting always just removes the card's own entry.
 * - Selecting a card whose businessId is already represented by a
 *   DIFFERENT card swaps it in for that entry (net-zero basket size).
 * - Selecting a genuinely new business grows the basket, up to the cap.
 */
export const applyBroadcastSelection = (
  current: BroadcastSelectionMap,
  entry: BroadcastSelectionEntry,
  isSelected: boolean
): ApplyBroadcastSelectionResult => {
  const key = `${entry.serviceId}|${entry.providerId}`;
  const next = new Map(current);

  if (!isSelected) {
    next.delete(key);
    return { selection: next, swapped: false };
  }

  const existingKeyForBusiness = entry.businessId
    ? [...next.entries()].find(
        ([k, v]) => v.businessId === entry.businessId && k !== key
      )?.[0]
    : undefined;

  if (existingKeyForBusiness) {
    next.delete(existingKeyForBusiness);
    next.set(key, entry);
    return { selection: next, swapped: true };
  }

  if (next.size < MAX_BROADCAST_SELECTIONS) {
    next.set(key, entry);
  }
  // Trying to grow beyond the cap with a genuinely new business: silently
  // ignore (the checkbox's disabled state prevents this too).
  return { selection: next, swapped: false };
};

/**
 * A card is disabled only when the basket is at the cap AND this card's
 * business isn't already in it — a card whose business IS already selected
 * must stay clickable, since checking it triggers a swap (net-zero to
 * basket size), not growth.
 */
export const isBroadcastSelectionDisabled = (
  current: BroadcastSelectionMap,
  serviceId: string,
  providerId: string,
  businessId: string
): boolean => {
  const key = `${serviceId}|${providerId}`;
  if (current.has(key)) return false;

  const businessAlreadySelected =
    !!businessId &&
    [...current.values()].some((v) => v.businessId === businessId);
  if (businessAlreadySelected) return false;

  return current.size >= MAX_BROADCAST_SELECTIONS;
};
